<template>
  <div
    class="video-player section-content--dark"
    :class="[
      $blockClass(blockClass, 'video-player'),
      {
        'video-player--block': blockStyle,
        'video-player--paused': paused
      }
    ]"
  >
    <div
      v-if="overlay && paused"
      class="video-player__overlay"
      @click="play"
    >
      <icon-play class="video-player__overlay-icon" />
    </div>
    <video
      ref="video"
      class="video-player__video"
      :autoplay="!reducedMotion && autoplay"
      :loop="loop"
      :muted="muted || autoplay"
      :poster="poster"
      :preload="preload"
      :playsinline="playsinline"
      @pause="handlePlayPauseEvent"
      @play="handlePlayPauseEvent"
      @timeupdate="handleTimeUpdateEvent"
      @durationchange="handleDurationChangeEvent"
      @click="play"
    >
      <source
        :src="src"
        type="video/mp4"
      >
    </video>

    <div
      v-if="controls"
      class="video-player__controls preserve-cursor"
    >
      <button
        type="button"
        class="video-player__status-button"
        @click="play"
      >
        <div
          v-if="paused"
          class="video-player__status-play"
        >
          <icon-play />
          <span
            class="visually-hidden"
            v-text="$string('accessibility.play')"
          />
        </div>
        <div
          v-else
          class="video-player__status-pause"
        >
          <icon-pause />
          <span
            class="visually-hidden"
            v-text="$string('accessibility.pause')"
          />
        </div>
      </button>
      <div class="video-player__progress-bar-outer">
        <div
          ref="progressBarInner"
          class="video-player__progress-bar-inner"
        />
      </div>
      <button
        type="button"
        class="video-player__audio-button"
        @click="isMuted = !isMuted"
      >
        <span
          v-if="!isMuted"
          class="video-player__audio-mute text-desktop-small-uc"
          v-text="$string('accessibility.mute')"
        />
        <span
          v-else
          class="video-player__audio-unmute text-desktop-small-uc"
          v-text="$string('accessibility.unmute')"
        />
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Video player (video-player)
 * -----------------------------------------------------------------------------
 * A custom video player with options.
 *
 * @param {String} src - The video file.
 * @param {String} [poster] - The poster image source.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean} controls - Enables the video controls.
 * @param {Boolean} autoplay - Starts playing the video as soon as it is ready,
 * except when reduced motion is enabled.
 * @param {Boolean} loop - Starts the video over again, every time it is
 * finished.
 * @param {Boolean} muted - Specifies that the audio output of the video should
 * be muted.
 * @param {Boolean} preload - Specifies if and how the author thinks the video
 * should be loaded when the page loads.
 * @param {Boolean} playsinline - Indicates that the video is to be played
 * within the element's playback area.
 * @param {Boolean} blockStyle - Styles the player as a block element.
 *
 */
import IconPlay from '~icons/direction/chevron/right.svg'
import IconPause from '~icons/misc/pause.svg'

export default {
  name: 'VideoPlayer',

  components: {
    IconPlay,
    IconPause,
  },

  props: {
    src: {
      type: String,
      default: '',
      required: true,
    },

    poster: {
      type: String,
      default: '',
    },

    blockClass: {
      type: String,
      default: '',
    },

    controls: {
      type: Boolean,
      default: true,
    },

    autoplay: {
      type: [Boolean, String],
      default: false,
    },

    loop: {
      type: [Boolean, String],
      default: true,
    },

    muted: {
      type: [Boolean, String],
      default: false,
    },

    preload: {
      type: [Boolean, String],
      default: false,
    },

    playsinline: {
      type: [Boolean, String],
      default: true,
    },

    blockStyle: {
      type: Boolean,
      default: false,
    },

    overlay: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentTime: 0,
      duration: 0,
      paused: true,
      elapsed: false,
      rangeWidth: 0,
      reducedMotion: true,
      hasAudio: false,
      isMuted: this.muted,
    }
  },

  mounted() {
    const video = this.$refs.video

    video.addEventListener('loadeddata', () => {
      setTimeout(() => {
        this.setHasAudio()
      }, this.$timing('quick'))
    })
    this.detectReducedMotion()
    video.addEventListener('timeupdate', () => this.videoProgress())

    if (!this.reducedMotion && this.autoplay) {
      this.paused = false
    }
  },

  methods: {

    /**
     * Plays the video instance.
     */
    play() {
      if (this.$refs.video.paused) {
        this.paused = false
        this.$refs.video.play()
        return
      }

      this.paused = true
      this.$refs.video.pause()
    },

    /**
     * Update video progress bar based on current time
     */
    videoProgress() {
      const video = this.$refs.video

      if (!video) {
        return
      }

      const currentTime = video.currentTime
      const duration = video.duration
      const progress = (100 * currentTime) / duration

      if (this.controls) {
        this.$refs.progressBarInner.style.width = `${progress}%`
      }
    },

    /**
     * Detects and sets the reduced motion option.
     */
    detectReducedMotion() {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    },

    /**
     * Sets a data param that indicates if the video has audio.
     */
    setHasAudio() {
      const video = this.$refs.video
      const hasAudio = video?.mozHasAudio || video?.webkitAudioDecodedByteCount > 0
      this.hasAudio = hasAudio
    },
  },
}
</script>

<style lang="scss">
@import './video-player';
</style>
